import * as React from 'react'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  List,
  DatagridConfigurable,
  SelectColumnsButton,
  useStore,
  FunctionField,
  ReferenceField,
  TextField,
  TopToolbar,
  TextInput,
  SelectInput,
  DateInput,
  ExportButton,
  useListFilterContext,
  downloadCSV,
  useGetList,
  useNotify,
  useRefresh,
  useDataProvider,
  Link,
  Pagination,
  useListContext,
} from 'react-admin'
import { fromTimestamp, formatDateToMMDDYYYY, formatPhoneDisplay } from '../../utils/helpers'
import ConvertOrderButton from '../buttons/ConvertOrderButton'

// Status chip styling
const REQUEST_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'warning', icon: <LocalShippingIcon fontSize="small" /> },
  convertedToOrder: { label: 'Order Created', color: 'success', icon: <LocalShippingIcon fontSize="small" /> },
  failedToCreate: { label: 'Failed to Create', color: 'error', icon: <ErrorOutlineIcon fontSize="small" /> },
  canceled: { label: 'Canceled', color: 'error', icon: <LocalShippingIcon fontSize="small" /> },
}

const FINANCIAL_STATUS_CONFIG = {
  none: { label: 'None', color: 'default', icon: <AttachMoneyIcon fontSize="small" /> },
  invoiced_partial: { label: 'Invoiced (Partial)', color: 'warning', icon: <AttachMoneyIcon fontSize="small" /> },
  invoiced: { label: 'Invoiced', color: 'success', icon: <AttachMoneyIcon fontSize="small" /> },
  paid: { label: 'Paid', color: 'success', icon: <AttachMoneyIcon fontSize="small" /> },
}

export const getFinancialStatus = (record) => {
  const shipped = record.shippedFinancialStatus || 'none'
  const tested = record.testedFinancialStatus || 'none'
  if (shipped === 'paid' && tested === 'paid') return 'paid'
  if (shipped === 'paid' || tested === 'paid') return 'invoiced_partial'
  if (shipped === 'invoiced' && tested === 'invoiced') return 'invoiced'
  if (shipped === 'invoiced' || tested === 'invoiced') return 'invoiced_partial'
  return 'none'
}

const StatusChip = ({ value, config }) => {
  const cfg = config[value] || { label: value || '—', color: 'default' }
  return <Chip icon={cfg.icon} label={cfg.badgeLabel || cfg.label} color={cfg.color} size="small" sx={{ fontWeight: 500, minWidth: 90 }} />
}

const ProviderSelectFilter = (props) => {
  const { data, isLoading } = useGetList('providers', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'displayName', order: 'ASC' },
  })
  return <SelectInput {...props} choices={data || []} optionText="displayName" isLoading={isLoading} emptyText="All Providers" />
}

const ProviderGroupSelectFilter = (props) => {
  const { data, isLoading } = useGetList('provider-groups', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  })
  // provider-groups records expose their PK as `id` (mapped by the data provider)
  // but kit-request records store the raw `providerGroupId` value — use optionValue="id"
  // since react-admin maps the PK to `id` on getList responses.
  return (
    <SelectInput {...props} choices={data || []} optionText="name" optionValue="id" isLoading={isLoading} emptyText="All Groups" />
  )
}

// Lightweight confirmation dialog
const ConfirmDialog = ({ open, title, message, onConfirm, onClose, loading }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText>{message}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>
        No, keep it
      </Button>
      <Button
        onClick={onConfirm}
        color="error"
        variant="contained"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {loading ? 'Processing…' : 'Yes, confirm'}
      </Button>
    </DialogActions>
  </Dialog>
)

// Row-level Cancel / Restore actions
// Cancel: available for pending + convertedToOrder
// Restore: available for canceled → convertedToOrder (if orderId present) or pending
const RowActions = ({ record }) => {
  const [dialog, setDialog] = useState(null) // 'cancel' | 'restore'
  const [loading, setLoading] = useState(false)
  const notify = useNotify()
  const refresh = useRefresh()
  const dataProvider = useDataProvider()

  const { requestStatus, orderId } = record
  const restoreTarget = orderId ? 'Order Created' : 'Pending'

  if (!['pending', 'convertedToOrder', 'canceled'].includes(requestStatus)) return null

  const handleAction = async (action) => {
    setLoading(true)
    try {
      await dataProvider.create(`kit-requests/${record.id}/${action}`, { data: {} })
      notify(action === 'cancel' ? 'Kit request canceled successfully.' : `Kit request restored to '${restoreTarget}'.`, {
        type: 'success',
      })
      setDialog(null)
      refresh()
    } catch (err) {
      console.error(`Error during ${action}:`, err)
      const msg = err?.response?.data?.message || err?.body?.message || `Failed to ${action} the kit request.`
      notify(msg, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box display="flex" gap={1} flexWrap="nowrap">
      {['pending', 'convertedToOrder'].includes(requestStatus) && (
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            setDialog('cancel')
          }}
          sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '12px' }}
          fullWidth
        >
          Cancel
        </Button>
      )}

      {requestStatus === 'canceled' && (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          onClick={(e) => {
            e.stopPropagation()
            setDialog('restore')
          }}
          sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '12px' }}
          fullWidth
        >
          Restore
        </Button>
      )}

      <ConfirmDialog
        open={dialog === 'cancel'}
        title="Cancel Kit Request"
        message="Are you sure you want to cancel this kit request?"
        loading={loading}
        onConfirm={() => handleAction('cancel')}
        onClose={() => setDialog(null)}
      />
      <ConfirmDialog
        open={dialog === 'restore'}
        title="Restore Kit Request"
        message={`Restore this canceled kit request back to '${restoreTarget}'?`}
        loading={loading}
        onConfirm={() => handleAction('restore')}
        onClose={() => setDialog(null)}
      />
    </Box>
  )
}

const ClearFiltersButton = () => {
  const { filterValues, setFilters } = useListFilterContext()
  const hasFilters = Object.keys(filterValues).length > 0
  if (!hasFilters) return null
  return (
    <Button size="small" variant="outlined" onClick={() => setFilters({}, [])} sx={{ textTransform: 'none', fontSize: '13px' }}>
      Clear Filters
    </Button>
  )
}

const TopPagination = () => {
  const { page, setPage, total, perPage } = useListContext()
  if (!total || total <= perPage) return null
  const totalPages = Math.ceil(total / perPage)
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Button size="small" variant="text" disabled={page <= 1} onClick={() => setPage(page - 1)} sx={{ minWidth: 0, p: '4px' }}>‹</Button>
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{page} / {totalPages}</Typography>
      <Button size="small" variant="text" disabled={page >= totalPages} onClick={() => setPage(page + 1)} sx={{ minWidth: 0, p: '4px' }}>›</Button>
    </Box>
  )
}

const FilteredDatagrid = ({ recordFilter, children, ...props }) => {
  const { data } = useListContext()
  const filtered = React.useMemo(() => {
    if (!recordFilter || !data) return undefined
    return data.filter(recordFilter)
  }, [data, recordFilter])

  return (
    <DatagridConfigurable {...props} data={filtered}>
      {children}
    </DatagridConfigurable>
  )
}

const KitRequestList = ({ filterDefaultValues, recordFilter } = {}) => {
  const notify = useNotify()
  const { pathname } = useLocation()
  const resource = 'kit-requests'
  const preferenceKey = `${resource}.datagrid`
  const [availableColumns] = useStore(`preferences.${preferenceKey}.availableColumns`, [])
  const [omit] = useStore(`preferences.${preferenceKey}.omit`, [])
  const [columns] = useStore(`preferences.${preferenceKey}.columns`)

  const kitRequestExporter = React.useCallback(
    (records) => {
      if (!records || records.length === 0) return

      // Get visible sources from store preferences
      const visibleSources =
        columns !== undefined
          ? columns.map((idx) => availableColumns.find((col) => col.index === idx)?.source).filter(Boolean)
          : availableColumns
              .filter((column) => !omit?.includes(column.source))
              .map((column) => column.source)
              .filter(Boolean)

      // Map source keys to CSV header names
      const sourceToHeaderMap = {
        requestId: ['Request ID'],
        providerGroupId: ['Provider Group ID'],
        clinicName: ['Clinic Name'],
        providerName: ["Provider Name"],
        createdAt: ['Date / Time'],
        requestStatus: ['Status', 'Financial Status'],
        patientName: ['Patient Name'],
        patientDob: ['Date of Birth'],
        dateOfVasectomy: ['Date of Vasectomy'],
        shippedDate: ['Ship Date'],
        targetConvertDate: ['Target Convert Date'],
        phoneNumber: ['Patient Phone', 'Patient Email'],
      }

      // Default headers to export
      let headersToExport = [
        'Request ID',
        'Provider Group ID',
        'Clinic Name',
        "Provider Name",
        'Date / Time',
        'Status',
        'Financial Status',
        'Patient Name',
        'Date of Birth',
        'Date of Vasectomy',
        'Ship Date',
        'Target Convert Date',
        'Patient Phone',
        'Patient Email',
      ]

      // If availableColumns are present in the preferences store, filter headers accordingly
      if (availableColumns && availableColumns.length > 0) {
        headersToExport = headersToExport.filter((header) => {
          return Object.entries(sourceToHeaderMap).some(([source, headers]) => {
            return visibleSources.includes(source) && headers.includes(header)
          })
        })
      }

      if (headersToExport.length === 0) {
        notify('No visible columns to export', { type: 'warning' })
        return
      }

      const rows = records.map((r) => {
        const row = {
          'Request ID': r.requestId || '',
          'Provider Group ID': r.providerGroupId || '',
          'Clinic Name': r.clinicName || '',
          "Provider Name": r.providerName || '',
          'Date / Time': r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
          Status: REQUEST_STATUS_CONFIG[r.requestStatus]?.label || r.requestStatus || '',
          'Financial Status': FINANCIAL_STATUS_CONFIG[r.shippedFinancialStatus]?.label || r.shippedFinancialStatus || '',
          'Patient Name': [r.patientFirstName, r.patientLastName].filter(Boolean).join(' ') || '',
          'Date of Birth': r.patientDob ? r.patientDob.slice(0, 10) : '',
          'Date of Vasectomy': r.dateOfVasectomy ? r.dateOfVasectomy.slice(0, 10) : '',
          'Ship Date': r.shippedDate ? r.shippedDate.slice(0, 10) : '',
          'Target Convert Date': r.targetConvertDate ? r.targetConvertDate.slice(0, 10) : '',
          'Patient Phone': r.phoneNumber || '',
          'Patient Email': r.email || '',
        }

        // Filter each row's fields to include only the visible headers
        const filteredRow = {}
        headersToExport.forEach((h) => {
          filteredRow[h] = row[h]
        })
        return filteredRow
      })

      const csvRows = [
        headersToExport.join(','),
        ...rows.map((row) => headersToExport.map((h) => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(',')),
      ]
      downloadCSV(csvRows.join('\n'), 'kit-requests')
    },
    [availableColumns, columns, omit, notify]
  )

  return (
    <Box>
      <List
        key={pathname}
        resource="kit-requests"
        disableSyncWithLocation
        filterDefaultValues={filterDefaultValues}
        filters={[
          <TextInput label="Request ID" source="requestId" alwaysOn sx={{ width: '250px' }} resettable />,
          <TextInput label="Patient Email" source="email" alwaysOn sx={{ width: '250px' }} resettable />,
          <TextInput label="Patient Name" source="patientName" alwaysOn sx={{ width: '250px' }} resettable />,
          <ProviderSelectFilter label="Provider" source="providerId" alwaysOn sx={{ width: '250px' }} resettable />,
          <SelectInput
            label="Status"
            source="requestStatus"
            choices={[
              { id: 'pending', name: 'Pending' },
              { id: 'convertedToOrder', name: 'Order Created' },
              { id: 'failedToCreate', name: 'Failed to Create' },
              { id: 'canceled', name: 'Canceled' },
            ]}
            alwaysOn
            sx={{ width: '250px' }}
            resettable
            emptyText="All Statuses"
          />,
          <ProviderGroupSelectFilter label="Provider Group" source="providerGroupId" alwaysOn sx={{ width: '250px' }} resettable />,
          <TextInput label="Clinic Name" source="clinicName" alwaysOn sx={{ width: '250px' }} resettable />,
          <DateInput label="Date Created" source="createdDate" alwaysOn sx={{ width: '210px' }} resettable />,
          <DateInput label="Date of Birth" source="patientDob" alwaysOn sx={{ width: '210px' }} resettable />,
          <TextInput label="Shipped Invoice ID" source="shippedInvoiceId" alwaysOn sx={{ width: '250px' }} resettable />,
          <TextInput label="Tested Invoice ID" source="testedInvoiceId" alwaysOn sx={{ width: '250px' }} resettable />,
        ].filter(Boolean)}
        actions={
          <TopToolbar>
            <ClearFiltersButton />
            <SelectColumnsButton />
            <ExportButton label="Export CSV" exporter={kitRequestExporter} />
            <TopPagination />
          </TopToolbar>
        }
        pagination={<Pagination rowsPerPageOptions={[50]} />}
        perPage={50}
        sort={{ field: 'createdAt', order: 'DESC' }}
        exporter={kitRequestExporter}
      >
        <FilteredDatagrid
          bulkActionButtons={false}
          omit={['providerGroupId', 'shippedDate', 'testedDate', 'shippedInvoiceId', 'testedInvoiceId']}
          recordFilter={recordFilter}
        >
          <FunctionField
            source="requestId"
            label="Request ID"
            sortable={false}
            render={(record) => {
              const id = record.requestId
              return (
                <Tooltip title={record.requestId} placement="top">
                  <Link to={`/kit-requests/${record.id}/show`} style={{ textDecoration: 'none' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {id && id.length > 6 ? `…` + id?.slice(-8) : id}
                    </Typography>
                  </Link>
                </Tooltip>
              )
            }}
          />

          <ReferenceField source="providerGroupId" reference="provider-groups" label="Provider Group" link="show" sortable={false}>
            <TextField source="name" />
          </ReferenceField>

          <FunctionField source="clinicName" label="Clinic Name" sortable={false} render={(record) => record.clinicName || '—'} />

          <FunctionField source="providerName" label="Provider Name" sortable={false} render={(record) => record.providerName || '—'} />

          <FunctionField
            source="createdAt"
            label="Date / Time"
            sortable={false}
            render={(record) => fromTimestamp(record.createdAt) || '—'}
          />

          <FunctionField
            source="requestStatus"
            label="Status"
            sortable={false}
            render={(record) => (
              <Box display="flex" flexDirection="column" gap={0.5}>
                <StatusChip value={record.requestStatus} config={REQUEST_STATUS_CONFIG} />
                <StatusChip value={getFinancialStatus(record)} config={FINANCIAL_STATUS_CONFIG} />
              </Box>
            )}
          />

          <FunctionField
            source="patientName"
            label="Patient Name"
            sortable={false}
            render={(record) => [record.patientFirstName, record.patientLastName].filter(Boolean).join(' ') || '—'}
          />

          <FunctionField
            source="patientDob"
            label="Date of Birth"
            sortable={false}
            render={(record) => formatDateToMMDDYYYY(record.patientDob) || '—'}
          />

          <FunctionField
            source="dateOfVasectomy"
            label="Date of Vasectomy"
            sortable={false}
            render={(record) => formatDateToMMDDYYYY(record.dateOfVasectomy) || '—'}
          />

          <FunctionField
            source="shippedDate"
            label="Ship Date"
            sortable={false}
            render={(record) => formatDateToMMDDYYYY(record.shippedDate) || '—'}
          />

          <FunctionField
            source="testedDate"
            label="Tested Date"
            sortable={false}
            render={(record) => formatDateToMMDDYYYY(record.testedDate) || '—'}
          />

          <FunctionField
            source="shippedInvoiceId"
            label="Shipped Invoice ID"
            sortable={false}
            render={(record) => record.shippedInvoiceId || '—'}
          />

          <FunctionField
            source="testedInvoiceId"
            label="Tested Invoice ID"
            sortable={false}
            render={(record) => record.testedInvoiceId || '—'}
          />

          <FunctionField
            source="targetConvertDate"
            label="Target Convert Date"
            sortable={false}
            render={(record) => formatDateToMMDDYYYY(record.targetConvertDate) || '—'}
          />

          <FunctionField
            source="phoneNumber"
            label="Patient Contact"
            sortable={false}
            render={(record) => (
              <Box display="flex" flexDirection="column">
                <Typography variant="body2">{formatPhoneDisplay(record.phoneNumber) || '—'}</Typography>
                <Typography variant="body2">{record.email || '—'}</Typography>
              </Box>
            )}
          />

          <FunctionField
            label="Actions"
            sortable={false}
            render={(record) => (
              <Box display="flex" flexDirection="column" gap={1}>
                <ConvertOrderButton record={record} />
                <RowActions record={record} />
              </Box>
            )}
          />
        </FilteredDatagrid>
      </List>
    </Box>
  )
}

export default KitRequestList
